import Imgix from '../core/Imgix'

class Grid {
    constructor(el) {
        this.grid = el
        this.options = JSON.parse(el.dataset.moduleOptions)
        this.itemsContainer = el.querySelector('[data-module="items"]')
        this.filter = el.querySelector("[data-module=filter]")
        this.pagination = el.querySelector("[data-module=pagination]")
        this.itemsCount = el.querySelector("[data-module=items-count]")
        this.itemsCountPlural = el.querySelector("[data-module=items-count-plural]")
        this.lastInternalHash = ''

        this.initialise()
    }

    initialise() {
        // populate items from json if the results are paginated
        if(this.options.pages > 1) {
            this.currentPage = 1;
            this.totalPages = this.options.pages;
            this.populate(window.grids[this.options.id]);
            this.updatePage(this.currentPage);
        }

        // note filter should show all avaialable filters irrespective of page
        if(this.filter) {
            this.initFilter();
        }
    }

    initFilter() {
        this.totalItems = 0

        const filterModal = this.filter.querySelector('[data-filter=modal]')

        if(filterModal) {
            this.filter.querySelectorAll('[data-filter=toggle]').forEach(el => el.addEventListener('click', (e) => {
                e.preventDefault();

                if(filterModal.open) {
                    filterModal.close()
                } else {
                    filterModal.showModal()
                }
            }))
        }

        this.filter.querySelectorAll("select, input").forEach((el) => {
            el.addEventListener("change", () => {
                this.filter.requestSubmit();
            })
        })

        this.filter.addEventListener("reset", (e) => {
            this.filter.requestSubmit();
        })

        this.filter.addEventListener("submit", (e) => {
            e.preventDefault();
            this.currentPage = 1;

            // if the grid has a transition property so it face animate between filtered states
            if(window.getComputedStyle(this.itemsContainer, null).getPropertyValue("transition")) {
                this.itemsContainer.classList.add('is-filtering')
            } else {
            // just apply the filter without animation
                this.applyFilter(true);
            }
        })

        this.itemsContainer.addEventListener('transitionend', () => {
            if(this.itemsContainer.classList.contains('is-filtering')) {
                this.applyFilter(true);
            }
        })

        document.addEventListener('filterApplied', () => {
            this.itemsContainer.classList.remove('is-filtering');
            if(this.itemsCount) this.itemsCount.innerHTML = this.totalItems
            this.itemsCountPlural.innerHTML = (this.totalItems > 1) ? 's' : '';
            document.dispatchEvent(new CustomEvent("revealOffcanvas"))
        })

        document.addEventListener('click', (e) => {
            const target = e.target,
                targetHref = target.getAttribute('href'),
                isHashQueryAnchor = (target.tagName === 'A' && targetHref?.includes('#?'));

            // Handle modifier keys (e.g. cmd/ctrl click, middle-click) and ignore if not a #? link
            if (!isHashQueryAnchor || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault()

            const currentHash = targetHref.split("#?")[1];

            if (currentHash !== undefined && currentHash !== '' && this.lastInternalHash.includes(currentHash)) return;

            const fullUrl = new URL(targetHref, window.location.origin).toString();

            window.location.assign(fullUrl);
            window.location.reload()
        });

        this.filters = Array.from(this.filter.querySelectorAll('[name]')).map(el => el.name);

        this.setFilterFromURL();
    }

    updatePage(page) {
        if(page != this.currentPage) {
            this.currentPage = page;
            this.applyFilter(true);
        }
        this.updatePagination();
    }

    updatePagination() {
        if(this.totalPages <= 1) {
            this.pagination.innerHTML = "";
            return
        }

        const itemHTML = wp.template('pagination')({
            pages: [...Array(this.totalPages).keys().map(i => i + 1)],
            current: this.currentPage
        })
        this.pagination.innerHTML = itemHTML;

        this.pagination.querySelectorAll('[data-page]').forEach((pageNum) => {
            pageNum.addEventListener('click', (e) => {
                this.updatePage(e.currentTarget.dataset.page)
            })
        })
    }

    populate(items) {
        this.itemsContainer.innerHTML = "";

        items.forEach(item => {
            const itemHTML = wp.template('grid-item-' + this.options.id)(item.item)
            this.itemsContainer.insertAdjacentHTML('beforeend', itemHTML);
        });
        new Imgix().process(this.itemsContainer)
    }

    getFilteredItems(formData) {
        const endpoint = "/wp-json/wp/v2/"+ this.options.endpoint +"/";
        const preFilters = this.options.preFilters
        const filters = {
            post_status: "publish",
            per_page: this.options.limit,           // Number of posts per page
            order: this.options.order,          // Order of posts (asc or desc)
            orderby: this.options.orderby,        // Order by date
            page: this.currentPage,
            response_format: 'article'
        };

        Object.keys(formData).forEach(category => {
            if(formData[category] != "") {
                filters[category] = formData[category]
            }
        });

        Object.keys(preFilters).forEach(category => {
            if(preFilters[category] != "") {
                filters[category + '_exclude'] = preFilters[category]
            }
        });

        const queryString = new URLSearchParams(filters).toString();
        const url = `${endpoint}?${queryString}`;

        // Use fetch to send a GET request to the WordPress REST API endpoint
        fetch(url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok ' + response.statusText);
                }

                this.totalItems = parseInt(response.headers.get('X-WP-Total'));
                this.totalPages = parseInt(response.headers.get('X-WP-TotalPages'));

                return response.json();
            })
            .then(data => {
                // Handle the JSON response data
                this.populate(data);
                this.filterApplied();
                this.updatePagination();
            })
            .catch(error => {
                // Handle any errors that occurred during the fetch
                console.error('There was a problem with the fetch operation:', error);
            });
    }

    setFilterFromURL() {
        let filters = Object.fromEntries(new URLSearchParams(window.location.hash.split("#?")[1]))

        this.filter.querySelectorAll("option, input").forEach((option) => {
            let name = option.name ? option.name : option.closest('select').name

            if(filters[name]) {
                option.checked = filters[name].split(',').includes(option.value)
                option.selected = filters[name].split(',').includes(option.value)
            }
        })

        if(filters['page']) {
            this.currentPage = parseInt(filters['page'])
        }

        this.applyFilter()
    }

    applyFilter(setURL) {
        let formData = this.filter ? Object.fromEntries(new FormData(this.filter)) : [];

        this.activeOptions = []

        if(this.options.pages) {
            this.getFilteredItems(formData)
            formData.page = this.currentPage
        } else {
            // Filter items where there is no pagination
            this.filterItems(formData)
        }

        if(setURL) {
            this.setURL(formData)
        }
    }

    filterApplied() {
        // feedback to other components that the filter has been applied
        document.dispatchEvent(new CustomEvent("filterApplied"))
    }

    setURL(formData) {
        const hash = "#?" + new URLSearchParams(formData).toString();
        this.lastInternalHash = hash;
        const url = window.location.origin + window.location.pathname + hash;

        window.history.pushState({}, '', url);
    }

    // Filter items where there is no pagination
    filterItems(formData) {
        const items = this.itemsContainer.querySelectorAll('[data-module=filter-item]')
        let itemsCount = 0

        items.forEach((item) => {
            if(typeof item.dataset.filters != "undefined") {
                let filters = JSON.parse(item.dataset.filters)

                // if the item filter includes every field in the form or the form field is unset, this item is active
                let isActive = Object.keys(formData).filter((value) => this.filters.includes(value)).every((field) => {
                    return formData[field] ? filters[field] && filters[field].includes(parseInt(formData[field])) : true;
                })

                item.classList.toggle('is-inactive', !isActive)

                if(isActive) {
                    item.setAttribute('data-index', itemsCount);
                    if (this.grid.parentElement.classList.contains('c-grid--gallery')) {
                        item.querySelector('.c-grid-item__media-container img').setAttribute('data-gallery-index', itemsCount)
                    }
                }

                itemsCount += (!isActive ? 0 : 1)
            }
        })

        this.totalItems = itemsCount

        this.filterApplied()
    }
}

export default Grid